import * as React from 'react';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import useMeasure from 'react-use-measure';
import { RenderPropSticky } from 'react-sticky-el';
import {
  Section,
  Container,
  Row,
  Spacer,
  Box,
  Column,
} from 'src/components-v2/Layout';
import TimelineSwitch from './TimelineSwitch';
import { ITimelineProps, Asset } from './Timeline.types';
import { useDeviceSize } from 'src/hooks';
import TimelineItem from './TimelineItem';
import { useCmsTheme } from 'src/contexts/CmsThemeContext/CmsThemeContextProvider';
import { CmsThemes } from 'src/lib/constants';
import { isAutomatedBrowser } from 'src/lib/browser-utils';
import { timelineMainColumnProps } from './Timeline.styles';

const Timeline: React.FunctionComponent<ITimelineProps> = ({
  heading,
  items,
  sx,
  sys,
  backgroundColor,
}) => {
  const inspectorProps = useContentfulInspectorMode({ entryId: sys?.id });
  const device = useDeviceSize();
  const isMobile = device.isSmall;
  const initialState = items.map(
    ({ id, isVisible, asset: { src, type } }): Asset => {
      return {
        id,
        src,
        isVisible,
        type,
      };
    },
  );
  const [assets, updateAssets] = React.useState(initialState);
  const observableAssetsRef = React.useRef(assets);
  const [isMeasuredRef, isMeasuredRefBounds] = useMeasure();
  const [timelineRef, timelineRefBounds] = useMeasure();
  const refs = React.useRef([]);
  const [spacerRef, spacerRefBounds] = useMeasure();
  const [headingRef] = useMeasure();
  const cmsTheme = useCmsTheme();
  const isV2 = cmsTheme === CmsThemes.WEB2;

  const updateState = React.useCallback(
    (id) => {
      updateAssets((state) => {
        return state.map((asset) => {
          return { ...asset, isVisible: asset.id === id };
        });
      });
    },
    [updateAssets],
  );

  const onObserve = React.useCallback(
    (entries) => {
      entries.some((entry) => {
        const asset = observableAssetsRef.current.find((asset) => {
          return asset.id === entry.target.id;
        });

        if (entry.isIntersecting && !asset.isVisible) {
          updateState(entry.target.id);

          // Exit loop as soon as we find the first intersection
          return true;
        }

        return false;
      });
    },
    [updateState],
  );

  React.useEffect(() => {
    if (isMobile) {
      return;
    }

    // Visual-regression captures scroll the whole page to trigger lazyloading,
    // which would march this observer through the steps and leave the captured
    // step — and with it the section height — up to timing. Stay on step 0.
    if (isAutomatedBrowser()) {
      return;
    }

    const nodes = refs.current;
    const observer = new IntersectionObserver(onObserve, {
      // Creates a region at the bottom half-ish of the viewport
      rootMargin: '-50% 0px -20% 0px',
      // Trigger observer on the first intersection
      threshold: 0,
    });

    nodes.forEach((node) => {
      observer.observe(node);
    });

    return () => {
      observer.disconnect();
    };
  }, [isMobile, onObserve]);

  React.useEffect(() => {
    // Break out `assets` into a stable ref to avoid recreating the `onObserve`
    // callback on every render, potentially triggering a loop via
    // repeatedly-mounted/unmounted `IntersectionObserver`s.
    observableAssetsRef.current = assets;
  }, [assets]);

  return (
    <Section
      sx={{
        '& h2': { mb: { xs: '6.6rem', sm: '5.4rem', md: '.8rem' } },
        ...sx,
      }}
      className='stepper'
      backgroundColor={backgroundColor}
    >
      {React.Children.map(heading, (child: React.ReactElement) => {
        // I'm leaving this here and all of the breakpoints in the event
        // we need to tweak these values some more
        // _clamp() will come in handy here
        // const headingHeight = +headingRefBounds.height;
        //
        // The margin depends on a two-hop measurement chain (asset stack →
        // spacer → margin), so whether it has settled by screenshot time is a
        // race — captured page heights flipped by exactly spacerHeight/2. Skip
        // it in automated browsers; the section just keeps the spacer's gap.
        const spacerMargin = isAutomatedBrowser()
          ? '0px'
          : `-${spacerRefBounds.height / 2}px`;
        return React.cloneElement(child, {
          ...child.props,
          ref: headingRef,
          sx: {
            mb: {
              xl: spacerMargin,
              lg: spacerMargin,
              md: spacerMargin,
              sm: spacerMargin,
              xs: spacerMargin,
            },
          },
        });
      })}
      <Container
        {...inspectorProps({
          fieldId: 'internalTitle',
        })}
      >
        <Row>
          {isV2 && (
            <Column
              ref={timelineRef}
              lgOffset={2}
              lg={1}
              smOffset={1}
              sx={{
                borderRight: 'solid 1px',
                borderImage: makeGradient(
                  backgroundColor ||
                    // @ts-ignore
                    sx?.backgroundColor ||
                    // @ts-ignore
                    sx?.bgColor ||
                    'black',
                ),
                display: { md: 'block', xs: 'none' },
              }}
            />
          )}
          <Column {...timelineMainColumnProps[cmsTheme]}>
            {items.map((item, index) => {
              return (
                <TimelineItem
                  key={`item-${item.id}-${index}`}
                  index={index}
                  item={item}
                  items={items}
                  refs={refs}
                  timelineRefBounds={timelineRefBounds}
                />
              );
            })}
          </Column>
          <Column
            lgOffset={1}
            lg={10}
            mdOffset={1}
            md={11}
            xsOffset={1}
            xs={22}
            sx={{
              display: { md: 'block', xs: 'none' },
            }}
          >
            <RenderPropSticky
              boundaryElement='.stepper'
              positionRecheckInterval={100}
              disabled={isMobile}
              hideOnBoundaryHit={false}
            >
              {({
                isFixed,
                wrapperStyles,
                wrapperRef,
                holderStyles,
                holderRef,
              }) => {
                return (
                  <div ref={holderRef} style={holderStyles}>
                    <div style={wrapperStyles} ref={wrapperRef}>
                      <Spacer
                        ref={spacerRef}
                        sx={{
                          height: `calc((100vh - ${isMeasuredRefBounds.height}px) / 2)`,
                        }}
                      />
                      <Box
                        ref={isMeasuredRef}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr',
                        }}
                      >
                        {items.map((item, index) => {
                          return (
                            <TimelineSwitch
                              asset={item}
                              assets={assets}
                              index={index}
                              key={`desktop-asset-${index}`}
                              sx={sx}
                            />
                          );
                        })}
                      </Box>
                    </div>
                  </div>
                );
              }}
            </RenderPropSticky>
          </Column>
        </Row>
      </Container>
    </Section>
  );
};

export default Timeline;

const makeGradient = (color) => {
  switch (color) {
    case 'gray':
    case 'Gray':
      return 'linear-gradient(to bottom, #f6f6f6, #C9C9C9, #f6f6f6) 7 100%';
    case 'white':
    case 'White':
      return 'linear-gradient(to bottom, #ffffff, #C9C9C9, #ffffff) 7 100%';
    default:
      return 'linear-gradient(to bottom, #111111, #383838, #111111) 7 100%';
  }
};
